import React, { useRef, useEffect, useContext } from 'react';
import { Map } from './Field.styles';
import { useTheme } from 'styled-components';
import toBBox from '@turf/bbox';
import L from 'leaflet';
import { MimeTypes, ApiType, LayersFactory } from '@sentinel-hub/sentinelhub-js';
import { ShAuthContext } from '../../contexts/shAuthContext';
import dayjs from 'dayjs';
import { TRUE_COLOR_BASE_LAYER } from './constants/performance-index-layers';
import useSoilTypes from '../../hooks/useSoilTypes';
import useCropTypes from '../../hooks/useCropTypes';
import { t } from 'ttag';
import { getHarvestDateTypeByCode, getSeedDateTypeByCode } from './constants/filters';
import { feature, featureCollection } from '@turf/helpers';

const MAPTILER_BASELAYER = {
  SATFARM_INTERNAL_ID: 'VOYAGER_MAP',
  style: `https://api.maptiler.com/maps/voyager/style.json?key=${process.env.REACT_APP_MAPTILER_TOKEN}`,
};

export default function FieldMap({
  field,
  neighbouringFields,
  children,
  isAnalysingMultipleFields,
  activeLayer,
  date,
  onFieldClick,
}) {
  const mapRef = useRef();
  const leafletMap = useRef();
  const theme = useTheme();
  const neigbouringFieldsRef = useRef();
  const fieldRef = useRef();
  const fieldDataRef = useRef();
  const { shAuthToken } = useContext(ShAuthContext);

  const { getSoilTypeNameById } = useSoilTypes(field.scope);
  const { getCropNameById } = useCropTypes(field.scope);

  useEffect(() => {
    leafletMap.current = L.map(mapRef.current, {}).setView([47.076562, 15.436078], 11);

    L.maplibreGL(MAPTILER_BASELAYER).addTo(leafletMap.current);
    return () => leafletMap.current.remove();
  }, []);

  useEffect(() => {
    if (!field) {
      return;
    }

    const fieldStyle = {
      color: theme.fieldColor,
      weight: theme.fieldWeight,
      fillColor: 'none',
    };

    fieldRef.current = L.layerGroup().addTo(leafletMap.current);
    L.geoJson(field.geometry, fieldStyle)
      .bindTooltip(() => {
        const cropName = getCropNameById(field.cropId);
        const soilTypeName = getSoilTypeNameById(field.soilTypeId);
        const seedDateName = getSeedDateTypeByCode(field.att_seeddate_class);
        const harvestDateName = getHarvestDateTypeByCode(field.att_harvdate_class);

        return `<b>${t`Selected field`}</b><br><br>${t`Crop type`}: ${cropName}<br>${t`Soil type`}: ${soilTypeName}<br><br>${t`Seed date`}: ${seedDateName}<br>${t`Harvest date`}: ${harvestDateName}`;
      })
      .addTo(fieldRef.current);
    const bbox = toBBox(field.geometry);

    const leafletBBox = [
      [bbox[1], bbox[0]],
      [bbox[3], bbox[2]],
    ];
    const leafletLatLngBounds = L.latLngBounds(leafletBBox);
    leafletMap.current.fitBounds(leafletLatLngBounds, { padding: [100, 100] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  useEffect(() => {
    if (neigbouringFieldsRef.current) {
      leafletMap.current.removeLayer(neigbouringFieldsRef.current);
    }

    if (isAnalysingMultipleFields) {
      neigbouringFieldsRef.current = L.layerGroup().addTo(leafletMap.current);
      const neighbouringFieldStyle = {
        color: theme.neighbouringFieldColor,
        weight: theme.neighbouringFieldWeight,
        opacity: theme.neighbouringFieldOpacity,
      };

      neighbouringFields.forEach((field, index) => {
        const selectedNeighbouringFieldStyle = {
          color: theme[`selectedNeighbouringFieldColor_${index % 5}`],
          weight: theme.selectedNeighbouringFieldWeight,
          opacity: theme.selectedNeighbouringFieldOpacity,
        };

        L.geoJson(field.geometry, {
          isSelected: field.isSelected,
          style: field.isSelected ? selectedNeighbouringFieldStyle : neighbouringFieldStyle,
          satfarmId: field.id,
          onEachFeature: (feature, layer) => {
            layer.on({
              click: (event) => {
                const { isSelected, satfarmId } = event.target.options;
                handleNeighbouringFieldClick(isSelected, satfarmId);
              },
            });
          },
        })
          .bindTooltip(() => {
            const cropName = getCropNameById(field.cropId);
            const soilTypeName = getSoilTypeNameById(field.soilTypeId);
            const seedDateName = getSeedDateTypeByCode(field.att_seeddate_class);
            const harvestDateName = getHarvestDateTypeByCode(field.att_harvdate_class);

            return `<b>${t`Neighbouring field`} ${
              index + 1
            }</b><br><br>${t`Crop type`}: ${cropName}<br>${t`Soil type`}: ${soilTypeName}<br><br>${t`Seed date`}: ${seedDateName}<br>${t`Harvest date`}: ${harvestDateName}`;
          })
          .addTo(neigbouringFieldsRef.current);

        fetchFieldData(field, activeLayer, date).then((data) => {
          L.imageOverlay(data.url, data.bbox).addTo(neigbouringFieldsRef.current);
        });
      });

      if (neighbouringFields.length > 0) {
        const fieldFeatureCollection = featureCollection(
          neighbouringFields.map((field) => feature(field.geometry)),
        );
        const bbox = toBBox(fieldFeatureCollection);
        const leafletBBox = [
          [bbox[1], bbox[0]],
          [bbox[3], bbox[2]],
        ];
        const leafletLatLngBounds = L.latLngBounds(leafletBBox);
        leafletMap.current.fitBounds(leafletLatLngBounds);
      }
    } else {
      const bbox = toBBox(field.geometry);
      const leafletBBox = [
        [bbox[1], bbox[0]],
        [bbox[3], bbox[2]],
      ];
      const leafletLatLngBounds = L.latLngBounds(leafletBBox);
      leafletMap.current.fitBounds(leafletLatLngBounds, { padding: [100, 100] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighbouringFields, isAnalysingMultipleFields, activeLayer, date]);

  useEffect(() => {
    if (fieldDataRef.current) {
      leafletMap.current.removeLayer(fieldDataRef.current);
    }

    fieldDataRef.current = L.layerGroup().addTo(leafletMap.current);

    if (activeLayer === TRUE_COLOR_BASE_LAYER.id) {
      const performanceIndexConfig = {
        layers: activeLayer,
        format: 'image/jpg',
        time: `${dayjs(date).format('YYYY-MM-DD')}/${dayjs(date).format('YYYY-MM-DD')}`,
      };

      fieldDataRef.current = L.layerGroup().addTo(leafletMap.current);

      try {
        L.tileLayer
          .wms(process.env.REACT_APP_SENTINEL2_WMS_ENDPOINT, performanceIndexConfig)
          .addTo(fieldDataRef.current);
      } catch (error) {
        console.log(error);
      }
    } else {
      fetchFieldData(field, activeLayer, date).then((data) => {
        L.imageOverlay(data.url, data.bbox).addTo(fieldDataRef.current);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayer, date]);

  async function fetchFieldData(field, activeLayer, date) {
    const bbox = toBBox(field.geometry);

    const leafletBBox = [
      [bbox[1], bbox[0]],
      [bbox[3], bbox[2]],
    ];

    const getMapParams = {
      geometry: field.geometry,
      fromTime: new Date(Date.UTC(date.getFullYear(), date.getMonth() - 1, date.getDate(), 0, 0, 0)),
      toTime: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)),
      width: 512,
      height: 512,
      format: MimeTypes.PNG,
    };

    try {
      const layer = await LayersFactory.makeLayer(process.env.REACT_APP_SENTINEL2_WMS_ENDPOINT, activeLayer);

      const imageBlob = await layer.getMap(getMapParams, ApiType.PROCESSING, { authToken: shAuthToken });
      const imageUrl = URL.createObjectURL(imageBlob);

      return {
        url: imageUrl,
        bbox: leafletBBox,
      };
    } catch (error) {
      console.error(error);
    }
  }

  function handleNeighbouringFieldClick(isSelected, fieldId) {
    const newNeighbouringFields = neighbouringFields.map((field) => {
      const { id } = field;
      if (fieldId !== id) {
        return field;
      }

      return {
        ...field,
        isSelected: !isSelected,
      };
    });
    onFieldClick(newNeighbouringFields);
  }

  return <Map ref={(el) => (mapRef.current = el)}>{children}</Map>;
}
